import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import dayjs from "dayjs";
import { TZ, formatDateKey, maxDateKey, minDateKey, todayKey } from "./model";

function CalendarIcon() {
  return (
    <svg className="kbv-kq-dates-icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3.5 10h17M8 3.5v3.5M16 3.5v3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

const WEEK_LABELS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];

function monthCells(cursor, min, max) {
  const start = dayjs.tz(cursor, TZ).startOf("month");
  const lead = (start.day() + 6) % 7;
  const cells = Array.from({ length: lead }, () => null);
  const count = start.daysInMonth();
  for (let day = 1; day <= count; day += 1) {
    const key = start.date(day).format("YYYY-MM-DD");
    cells.push({ key, day, disabled: key < min || key > max });
  }
  return cells;
}

function Calendar({ value, min, max, onPick }) {
  const seed = value && value >= min && value <= max ? value : max;
  const [cursor, setCursor] = useState(() => dayjs.tz(seed, TZ).startOf("month").format("YYYY-MM-DD"));
  const view = dayjs.tz(cursor, TZ).startOf("month");
  const prevDisabled = view.subtract(1, "day").format("YYYY-MM-DD") < min;
  const nextDisabled = view.add(1, "month").format("YYYY-MM-DD") > max;
  const cells = monthCells(cursor, min, max);

  return (
    <div className="kbv-kq-cal" role="dialog" aria-label="Chọn ngày">
      <div className="kbv-kq-cal-head">
        <button
          type="button"
          className="kbv-kq-cal-nav"
          aria-label="Tháng trước"
          disabled={prevDisabled}
          onClick={() => setCursor(view.subtract(1, "month").format("YYYY-MM-DD"))}
        >
          ‹
        </button>
        <span className="kbv-kq-cal-title">{`Tháng ${view.month() + 1} ${view.year()}`}</span>
        <button
          type="button"
          className="kbv-kq-cal-nav"
          aria-label="Tháng sau"
          disabled={nextDisabled}
          onClick={() => setCursor(view.add(1, "month").format("YYYY-MM-DD"))}
        >
          ›
        </button>
      </div>
      <div className="kbv-kq-cal-grid">
        {WEEK_LABELS.map((label) => (
          <span key={label} className="kbv-kq-cal-dow">{label}</span>
        ))}
        {cells.map((cell, index) => (
          cell ? (
            <button
              key={cell.key}
              type="button"
              className={clsx(
                "kbv-kq-cal-day",
                cell.key === value && "is-active",
                cell.key === max && "is-today"
              )}
              disabled={cell.disabled}
              onClick={() => onPick(cell.key)}
            >
              {cell.day}
            </button>
          ) : (
            <span key={`e-${index}`} className="kbv-kq-cal-empty" />
          )
        ))}
      </div>
    </div>
  );
}

function DateField({ active, label, ariaLabel, value, min, max, onPick }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="kbv-kq-dates-picker" ref={wrapRef}>
      <button
        type="button"
        className={clsx("kbv-kq-dates-custom", (active || open) && "is-active")}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((next) => !next)}
      >
        <CalendarIcon />
        <span className="kbv-kq-dates-custom-text">{label}</span>
      </button>
      {open ? (
        <Calendar
          value={value}
          min={min}
          max={max}
          onPick={(key) => {
            setOpen(false);
            onPick(key);
          }}
        />
      ) : null}
    </div>
  );
}

export default function DateChips({ days, value, onChange, direction = "backward" }) {
  const forward = direction === "forward";
  const max = forward ? maxDateKey() : todayKey();
  const min = forward ? todayKey() : minDateKey();
  const mobileDays = days.slice(0, 4);
  const desktopDays = days.slice(0, 7);
  const inChips = desktopDays.some((day) => day.key === value);
  const inMobile = mobileDays.some((day) => day.key === value);
  const ariaFor = (preset) => (preset ? "Chọn ngày" : `Chọn ngày ${formatDateKey(value, "DD/MM/YYYY")}`);

  const pick = (key) => {
    if (!key || key < min || key > max) return;
    if (key !== value) onChange(key);
  };

  return (
    <div className="kbv-kq-dates">
      <div className="kbv-kq-dates-mobile">
        <div className="kbv-kq-dates-mobile-row">
          {mobileDays.map((day) => (
            <button
              key={day.key}
              type="button"
              className={clsx("kbv-kq-chip-sm", day.key === value && "is-active")}
              onClick={() => pick(day.key)}
            >
              <span className="kbv-kq-chip-sm-name">{day.short}</span>
              <span className="kbv-kq-chip-sm-sub">{day.dateLabel}</span>
            </button>
          ))}
          <DateField
            active={!inMobile}
            label={inMobile ? "Ngày khác" : formatDateKey(value, "DD/MM")}
            ariaLabel={ariaFor(inMobile)}
            value={value}
            min={min}
            max={max}
            onPick={pick}
          />
        </div>
      </div>

      <div className="kbv-kq-dates-desktop">
        <div className="kbv-kq-dates-track">
          {desktopDays.map((day) => (
            <button
              key={day.key}
              type="button"
              className={clsx("kbv-kq-chip", day.key === value && "is-active")}
              onClick={() => pick(day.key)}
            >
              {day.label}
            </button>
          ))}
        </div>
        <DateField
          active={!inChips}
          label={inChips ? "Chọn ngày" : formatDateKey(value, "DD/MM/YYYY")}
          ariaLabel={ariaFor(inChips)}
          value={value}
          min={min}
          max={max}
          onPick={pick}
        />
      </div>
    </div>
  );
}
